import { create } from 'zustand'

export type ScopedStringMap = Record<string, string[]>

export type AuthUser = {
  id: string
  name: string
  email: string
  roles: string[]
  permissions: string[]
  tenantIds: string[]
  globalRoles: string[]
  globalPermissions: string[]
  tenantRoles: ScopedStringMap
  tenantPermissions: ScopedStringMap
}

export type AuthUserSource = Omit<AuthUser, 'roles' | 'permissions' | 'tenantIds' | 'globalRoles' | 'globalPermissions' | 'tenantRoles' | 'tenantPermissions'> & {
  roles?: string[] | null
  permissions?: string[] | null
  tenantIds?: string[] | null
  globalRoles?: string[] | null
  globalPermissions?: string[] | null
  tenantRoles?: Record<string, string[] | null | undefined> | null
  tenantPermissions?: Record<string, string[] | null | undefined> | null
}

function normalizeStringList(value: string[] | null | undefined) {
  if (!Array.isArray(value)) return []
  return [...new Set(value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0).map((item) => item.trim()))].sort()
}

function normalizeStringMap(value: Record<string, string[] | null | undefined> | null | undefined) {
  if (!value || typeof value !== 'object') return {}
  return Object.fromEntries(Object.entries(value)
    .map(([key, items]) => [key.trim(), normalizeStringList(items)] as const)
    .filter(([key, items]) => key.length > 0 && items.length > 0))
}

function hasScopedPermissionGrants(user: AuthUser) {
  return user.globalPermissions.length > 0 || Object.keys(user.tenantPermissions).length > 0
}

function hasScopedRoleGrants(user: AuthUser) {
  return user.globalRoles.length > 0 || Object.keys(user.tenantRoles).length > 0
}

function mergeLists(...values: string[][]) {
  return normalizeStringList(values.flat())
}

function scopeAuthUser(user: AuthUser, tenantId: string): AuthUser {
  return {
    ...user,
    roles: hasScopedRoleGrants(user) ? mergeLists(user.globalRoles, user.tenantRoles[tenantId] ?? []) : user.roles,
    permissions: hasScopedPermissionGrants(user) ? mergeLists(user.globalPermissions, user.tenantPermissions[tenantId] ?? []) : user.permissions,
  }
}

export function authUserHasGlobalAccess(user: AuthUser) {
  return user.globalPermissions.includes('*') || !hasScopedPermissionGrants(user) && user.permissions.includes('*')
}

export function normalizeAuthUser(user: AuthUserSource): AuthUser {
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    roles: normalizeStringList(user.roles),
    permissions: normalizeStringList(user.permissions),
    tenantIds: normalizeStringList(user.tenantIds),
    globalRoles: normalizeStringList(user.globalRoles),
    globalPermissions: normalizeStringList(user.globalPermissions),
    tenantRoles: normalizeStringMap(user.tenantRoles),
    tenantPermissions: normalizeStringMap(user.tenantPermissions),
  }
}

type AuthState = {
  user: AuthUser | null
  csrfToken: string
  tenantId: string
  environmentId: string
  initialized: boolean
  setUser: (user: AuthUserSource | null) => void
  setCSRFToken: (csrfToken: string) => void
  setTenantId: (tenantId: string) => void
  setEnvironmentId: (environmentId: string) => void
  setInitialized: (initialized: boolean) => void
  reset: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  csrfToken: '',
  tenantId: '',
  environmentId: '',
  initialized: false,
  setUser: (source) => set((state) => {
    if (!source) return { user: null, csrfToken: '', tenantId: '', environmentId: '' }
    const normalized = normalizeAuthUser(source)
    const globalAccess = authUserHasGlobalAccess(normalized)
    const tenantId = state.tenantId && (globalAccess || normalized.tenantIds.includes(state.tenantId))
      ? state.tenantId
      : normalized.tenantIds[0] ?? ''
    return {
      user: scopeAuthUser(normalized, tenantId),
      tenantId,
      environmentId: tenantId === state.tenantId ? state.environmentId : '',
    }
  }),
  setCSRFToken: (csrfToken) => set({ csrfToken }),
  setTenantId: (tenantId) => set((state) => {
    if (!state.user) return { tenantId: state.tenantId }
    if (tenantId && !authUserHasGlobalAccess(state.user) && !state.user.tenantIds.includes(tenantId)) return { tenantId: state.tenantId }
    return {
      tenantId,
      environmentId: tenantId === state.tenantId ? state.environmentId : '',
      user: scopeAuthUser(state.user, tenantId),
    }
  }),
  setEnvironmentId: (environmentId) => set({ environmentId }),
  setInitialized: (initialized) => set({ initialized }),
  reset: () => set({ user: null, csrfToken: '', tenantId: '', environmentId: '', initialized: true }),
}))
